import React from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { BankSelect } from "../BankAccounts";

export const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
export const label = "block text-sm font-medium mb-1.5";
export const PAY_METHODS = ["cash", "bank", "upi", "qr"];
export const STATUS_OPTS = ["unpaid", "partial", "paid", "cancelled"];
export const INV_TYPES = ["Consolidated", "Spare Parts", "DEF"];
export const statusColors = {
  paid: "bg-[hsl(var(--positive))]/15 text-[hsl(var(--positive))]",
  partial: "bg-accent/15 text-accent",
  unpaid: "bg-destructive/10 text-destructive",
};

export const toggleBtn = (active) =>
  `rounded-md border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`;

export const rowsTotal = (rows) => rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

// Shows toast for direct save vs. sent-for-approval; returns true when applied.
export const applyResult = (res, okMsg, reloadLocks) => {
  if (res.data?.status === "pending") { toast.info(res.data.message); reloadLocks(); return false; }
  toast.success(okMsg);
  return true;
};

export const MethodPicker = ({ value, onChange, testid, size = "h-9" }) => (
  <div className="grid grid-cols-4 gap-1 flex-1">
    {PAY_METHODS.map((m) => (
      <button key={m} type="button" data-testid={`${testid}-${m}`} onClick={() => onChange(m)}
        className={`${size} text-xs uppercase ${toggleBtn(value === m)}`}>{m}</button>
    ))}
  </div>
);

const SplitRow = ({ r, i, prefix, refPlaceholder, canRemove, setRow, removeRow }) => (
  <div data-testid={`${prefix}-row-${i}`} className="rounded-md border border-border p-2.5 space-y-2">
    <div className="flex items-center gap-2">
      <MethodPicker value={r.method} testid={`${prefix}-method-${i}`} onChange={(m) => setRow(i, { method: m })} />
      {canRemove && (
        <button data-testid={`${prefix}-remove-${i}`} onClick={() => removeRow(i)} className="shrink-0 h-9 w-9 rounded-md border border-input flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"><X size={15} /></button>
      )}
    </div>
    <div className="grid grid-cols-2 gap-2">
      <input data-testid={`${prefix}-amount-${i}`} type="number" step="0.01" min="0" className={field} placeholder="Amount (₹)"
        value={r.amount} onChange={(e) => setRow(i, { amount: e.target.value })} />
      <input data-testid={`${prefix}-reference-${i}`} className={field}
        placeholder={r.method === "cash" ? refPlaceholder : "Reference / UTR no."}
        disabled={r.method === "cash"} value={r.reference} onChange={(e) => setRow(i, { reference: e.target.value })} />
    </div>
    {r.method !== "cash" && <BankSelect testid={`${prefix}-bank-${i}`} className="mt-2" value={r.bank_account} onChange={(v) => setRow(i, { bank_account: v })} />}
  </div>
);

export const SplitRows = ({ rows, setRows, prefix, addTestid, addMethod = "cash", refPlaceholder = "No reference" }) => {
  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  return (
    <>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <SplitRow key={i} r={r} i={i} prefix={prefix} refPlaceholder={refPlaceholder} canRemove={rows.length > 1} setRow={setRow} removeRow={removeRow} />
        ))}
      </div>
      <button data-testid={addTestid} onClick={() => setRows((rs) => [...rs, { method: addMethod, amount: "", reference: "" }])}
        className="mt-2 w-full h-9 rounded-md border border-dashed border-input text-sm flex items-center justify-center gap-1.5 text-muted-foreground hover:bg-muted transition-colors">
        <Plus size={14} /> Add another method
      </button>
    </>
  );
};

export const BalanceBox = ({ title = "Outstanding balance", children }) => (
  <div className="rounded-md bg-muted p-3 text-sm flex justify-between">
    <span className="text-muted-foreground">{title}</span>
    {children}
  </div>
);
